/**
 * `createApiResearchSource` — `seed-mgmt-api` research requests: a person asking
 * who a set of addresses are (the "Identify addresses" action, wherever it is
 * offered — `features/research`).
 *
 * Each address becomes an ordinary exploration on the same path the pipeline's
 * research takes, spending one budget, and every finding lands in the
 * requester's three evidence bands — written when a source trusted to write
 * scores at or above the trusted score, otherwise queued for Review, dropped
 * below the floor (counted: `recordsBelowFloor`). Findings never leave the
 * requester's tenancy: the team, or the requester alone (`audience: "user"`).
 *
 * Canonical `/seed-research` paths are rewritten onto `internal/seed-mgmt-api`
 * (`/v0/seed/research`) by `RESOURCE_REWRITES`.
 *
 * Progress is DERIVED server-side from the request's runs on every read, so a
 * poll is a complete protocol on its own: `status` moves `running` → `done` /
 * `partial` / `failed`, and `not_started` means no address got a run (over
 * budget, research switched off) — each subject's `outcome` says which. The
 * research stream (`features/research/researchStream`) pushes the same progress
 * when it is deployed; the poll stays as the fallback.
 *
 * Any context can ask: a case, a tracer, an address's own page, or the caller's
 * own ad-hoc selection (`ResearchContext`).
 *
 * ## Retry
 *
 * A request that failed — wholly or in part — is replayable in place
 * (`retry`): the server re-runs only what failed (a failed run, a chain the
 * research had followed the address onto, an address that could not be
 * started), under the same request, and never repeats what succeeded. The row
 * goes back to `running`; the failures it replaced stop counting. Every read
 * says how much a retry would start (`retryable`) — the one thing a client
 * offers the action on — and how often the request was retried (`retries`).
 *
 * ## The forensic profile
 *
 * `profile: "forensic"` asks each address's run to also read the address's own
 * transaction history and run the address-forensics checks (an exchange
 * deposit address, an address-poisoning lookalike). It costs more of the same
 * research budget and runs on the heavy lane; its verdicts are catalog claims
 * that meet the same three bands. Each subject then carries the run's
 * forensic record (`subjects[].forensics`): every finding with what became of
 * each of its claims, and every check and evidence source that declined, with
 * why. A server that predates the profile sends neither field — read as a
 * standard request with no record, never as "the checks found nothing".
 *
 * ## Which checks
 *
 * Every request runs address-forensics checks on each address — `standard`
 * the cheap fact checks (what its code says it is, which factory made it, a
 * counterfeit or spam token), `forensic` all of them. `checks` narrows the
 * profile's set to the ids a reader picked (`@authe/fe-commons/forensics`, the
 * generated catalogue); it is sent only when the reader changed the default
 * set, and the server refuses (400) an id it does not know or one the profile
 * does not run. The request echoes it back (`checks`, `[]` = the defaults); a
 * server that predates the selection sends no `checks` at all — `null` here,
 * so the dialog can say the pick was not applied.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";
import { normalizeAddress } from "@authe/fe-commons/chains";
import {
  FORENSIC_SEVERITIES,
  forensicSeverityRank,
  type ForensicSeverity,
} from "@authe/fe-commons/storage";

import { asNumber, asRecord, asString, rowsAt } from "./wire";

/** Canonical collection path; `RESOURCE_REWRITES` maps it to the service. */
export const RESEARCH_PATH = "/seed-research";

/** `seed.research.max_subjects`' default — the server refuses more in one request. */
export const RESEARCH_MAX_SUBJECTS = 50;

export type ResearchAudience = "tenant" | "user";

/**
 * What a research request works for — the thing that lists it again
 * (`commons.jobs.context`'s kind registry).
 *
 * - `investigation` — a case (`inv_…`), its "Identify addresses" action.
 * - `tracer` — a tracer (`/tracers/$id`): "who are the addresses on this trail?".
 * - `personal` — an ad-hoc selection from a table. The server fills the id with
 *   the caller's own user id, so it is never sent, and the list answers only the
 *   caller's own requests.
 * - `address` — an address's own page ("Identify" on it). The id is the address,
 *   keyed the way the server keys it (EVM hex folded, base58 kept — no chain:
 *   the page is one address on every chain it is on), so the page lists what
 *   anyone in the workspace already asked about it. Build it with
 *   {@link addressResearchContext}.
 */
export type ResearchContext =
  | { kind: "investigation"; id: string }
  | { kind: "tracer"; id: string }
  | { kind: "address"; id: string }
  | { kind: "personal" };

export type ResearchContextKind = ResearchContext["kind"];

/**
 * The context of one address's own page. The id is folded here, once, the way
 * `commons.jobs.context` folds it (`commons.keys.normalize_address`, no
 * chain), so the checksummed and the lower-case spelling of one EVM address
 * are one list — and a base58 address is never lower-cased into another one.
 */
export function addressResearchContext(address: string): ResearchContext {
  return { kind: "address", id: normalizeAddress(address) };
}

/** A context's id as the server keys it: a tracer's uuid and an address fold, a case id does not. */
function contextId(context: Exclude<ResearchContext, { kind: "personal" }>): string {
  if (context.kind === "tracer") return context.id.toLowerCase();
  if (context.kind === "address") return normalizeAddress(context.id);
  return context.id;
}

/**
 * The list filter's wire form: `<kind>:<id>`, or the bare kind for `personal`
 * (the server reads the id off the caller).
 */
export function researchContextRef(context: ResearchContext): string {
  if (context.kind === "personal") return "personal";
  return `${context.kind}:${contextId(context)}`;
}

/**
 * The create body's `context` — `personal` carries no id (the server fills
 * the caller's). A tracer's uuid and an address are sent folded, as the server
 * keys them.
 */
function researchContextBody(context: ResearchContext): Record<string, string> {
  if (context.kind === "personal") return { kind: "personal" };
  return { kind: context.kind, id: contextId(context) };
}

/**
 * Whether a request (as read back, or pushed) was made for `context` — what a
 * jobs panel filters the research stream by: a tenant-audience push reaches
 * every member of the tenant, whatever it was asked for. A personal request's
 * id is the server-filled user id, compared with the caller's when known.
 */
export function researchContextMatches(
  stated: { kind: string; id?: string | null } | null | undefined,
  context: ResearchContext,
  /** The caller's user id, when known: a personal request is the caller's only when its id is theirs. */
  callerUserId?: string | null,
): boolean {
  if (!stated || stated.kind !== context.kind) return false;
  // Ids compare folded: the server folds a tracer's uuid to lower case.
  if (context.kind === "personal") {
    return !callerUserId || !stated.id || stated.id.toLowerCase() === callerUserId.toLowerCase();
  }
  // An address folds by its own rule, never by a blanket lower-case: two
  // base58 addresses that differ only in case are two addresses.
  if (context.kind === "address") return normalizeAddress(stated.id ?? "") === contextId(context);
  return (stated.id ?? "").toLowerCase() === context.id.toLowerCase();
}

/**
 * How deep each address's research goes. `standard` asks the data providers;
 * `forensic` also reads the address's own history and runs the
 * address-forensics checks — heavier, and weighted in the research budget.
 */
export type ResearchProfile = "standard" | "forensic";

const PROFILES: readonly ResearchProfile[] = ["standard", "forensic"];

/**
 * What became of one claim a check proposed (`claim_outcome` in the seed
 * worker's catalog hand-off): written live, queued for Review, dropped under
 * the requester's minimum score, or not recorded for another reason.
 */
export type ForensicClaimOutcome = "written" | "review" | "dropped" | "ignored";

const CLAIM_OUTCOMES: readonly ForensicClaimOutcome[] = ["written", "review", "dropped", "ignored"];

/** One catalog claim a forensic check proposed — an entity link, a label or a tag. */
export interface ForensicClaimRecord {
  /** `entity` | `label` | `tag` — kept verbatim when this build does not know it. */
  kind: string;
  /** The venue, label or tag named. May quote chain-written text: render as text. */
  name: string;
  /** The source the catalog judged it under (`forensic_deposit_address`, …). */
  sourceId: string;
  confidence: number | null;
  /** `null` when the record does not say — never guessed as "written". */
  outcome: ForensicClaimOutcome | null;
}

/** One verdict a check reached about an address. */
export interface AddressForensicFinding {
  /** Namespaced by family: `evm.exchange_deposit_address`. */
  kind: string;
  severity: ForensicSeverity;
  /** One sentence. May quote chain-written text: render as text. */
  summary: string;
  foundBy: string;
  confidence: number | null;
  /** The bounded measurements, as stored. */
  facts: Record<string, unknown>;
  claims: ForensicClaimRecord[];
}

/** What one check or evidence source did — including when it did nothing. */
export interface AddressForensicRun {
  id: string;
  /** `ok` | `skipped` | `unavailable` | `failed` | `timeout` — kept verbatim. */
  status: string;
  reason: string | null;
  version: string | null;
  durationMs: number | null;
}

/** A subject's forensic record (`address_forensics_record`, version 1). */
export interface AddressForensics {
  /** `eip155:1:0x…`, as the worker spelled it. */
  subject: string;
  family: string | null;
  profile: string | null;
  highestSeverity: ForensicSeverity | null;
  /** Loudest first. */
  findings: AddressForensicFinding[];
  /** How many there were before the stored cap — never less than `findings.length`. */
  findingsTotal: number;
  probes: AddressForensicRun[];
  sources: AddressForensicRun[];
}

export type ResearchStatus = "running" | "done" | "partial" | "failed" | "not_started";

/** What asking for one address came to (`research.models.SubjectOutcome`). */
export type ResearchOutcome =
  | "pending"
  | "started"
  | "reused"
  | "over_budget"
  | "disabled"
  | "failed";

export interface ResearchSubjectInput {
  chainId: string;
  address: string;
}

export interface ResearchSubject extends ResearchSubjectInput {
  outcome: ResearchOutcome;
  runId: string | null;
  error: string | null;
  /** The run's own status (`queued` / `running` / `succeeded` / `failed` / …). */
  runStatus: string | null;
  /**
   * Whether this address's research has finished, as the server judges it — the
   * flag an address's queries are refreshed on. `null` from a server that
   * predates it (the run status then decides).
   */
  finished?: boolean | null;
  recordsWritten: number;
  recordsReviewQueued: number;
  /** Findings that scored under the requester's minimum score and were not recorded. */
  recordsBelowFloor: number;
  /** This address's forensic claims written live / queued for Review. Absent before the checks existed. */
  forensicClaimsWritten?: number;
  forensicClaimsReview?: number;
  /** The profile this address was researched at; absent from a server that predates it. */
  profile?: ResearchProfile;
  /**
   * The run's forensic record for this address — `null` when there is none
   * (a standard request, a run still going, a server that predates the checks).
   */
  forensics?: AddressForensics | null;
}

export interface ResearchCounts {
  runs: number;
  active: number;
  succeeded: number;
  failed: number;
  cancelled: number;
  /** Runs for chains discovery found the addresses live on, beyond the ones asked. */
  fanout: number;
  recordsWritten: number;
  recordsReviewQueued: number;
  recordsAutoApproved: number;
  recordsWithheldPrivate: number;
  /**
   * Catalog findings (entity / label / tag) that scored under the requester's
   * minimum score (`confidence_floor`) and so were not recorded — found, but
   * not "nothing found".
   */
  recordsBelowFloor: number;
  /** Forensic checks' catalog claims written live. Absent before the checks existed. */
  forensicClaimsWritten?: number;
  /** Forensic checks' catalog claims queued for Review. */
  forensicClaimsReview?: number;
}

export interface ResearchRequest {
  requestId: string;
  status: ResearchStatus;
  audience: ResearchAudience;
  /** `standard` for a request whose row names none (every request before the profile). */
  profile: ResearchProfile;
  /**
   * The check ids the reader picked, `[]` for the profile's defaults — or
   * `null` from a server that predates the selection (it ran the defaults).
   */
  checks: string[] | null;
  /** What it works for; `label` is the server's word for the kind ("Case", "Trace", "Personal"). */
  context: { kind: string; id: string; ref: string; label?: string } | null;
  requestedBy: string | null;
  createdAt: number;
  updatedAt: number;
  subjects: ResearchSubject[];
  counts: ResearchCounts;
  research: { budget: number; used: number };
  /**
   * How much research a retry would start right now — failed runs to replay
   * plus addresses to ask again; `0` when there is nothing to retry (nothing
   * failed, the request was cancelled, or it has used its retries). Absent
   * from a server that predates retry, and from a push that does not say:
   * never read as "nothing to retry" over a value already known.
   */
  retryable?: number;
  /** How many times the request was retried. Absent like `retryable`. */
  retries?: number;
}

export interface CreateResearchInput {
  subjects: ResearchSubjectInput[];
  context?: ResearchContext;
  audience?: ResearchAudience;
  /** Omitted is the server's default, `standard`. */
  profile?: ResearchProfile;
  /**
   * The check ids to run, narrowing the profile's defaults. Omitted (the usual
   * case) runs the defaults; never empty — the server refuses `[]`.
   */
  checks?: readonly string[];
  /** Stable per attempt, so a retry or a double-click researches nothing twice. */
  idempotencyKey?: string;
}

export interface ResearchSource {
  create(input: CreateResearchInput): Promise<{ created: boolean; request: ResearchRequest }>;
  get(requestId: string, opts?: { signal?: AbortSignal }): Promise<ResearchRequest>;
  /**
   * The requests one context asked for — a case, a tracer, the caller's own
   * ad-hoc selections — newest first, within the caller's tenancy.
   */
  listForContext(
    context: ResearchContext,
    opts?: { limit?: number; signal?: AbortSignal },
  ): Promise<ResearchRequest[]>;
  /**
   * Set what failed going again, under the same request. `started` is how much
   * research the call started — `0` when there was nothing to retry (or another
   * tab's retry is already running it), which is an answer, not an error.
   */
  retry(requestId: string): Promise<{ started: number; request: ResearchRequest }>;
}

const STATUSES: readonly ResearchStatus[] = ["running", "done", "partial", "failed", "not_started"];
const OUTCOMES: readonly ResearchOutcome[] = [
  "pending",
  "started",
  "reused",
  "over_budget",
  "disabled",
  "failed",
];

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

/** Mirrors the seed worker's bound on a stored record (`≤ 16 findings each`), with room. */
const MAX_FINDINGS = 32;
const MAX_RUNS = 32;
const MAX_CLAIMS = 16;

function optString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function forensicRunFromWire(raw: Record<string, unknown>): AddressForensicRun | null {
  const id = optString(raw.id);
  const status = optString(raw.status);
  if (!id || !status) return null;
  return {
    id,
    status,
    reason: optString(raw.reason),
    version: raw.version == null ? null : asString(raw.version),
    durationMs: asNumber(raw.duration_ms),
  };
}

function claimFromWire(raw: Record<string, unknown>): ForensicClaimRecord | null {
  const name = optString(raw.name);
  if (!name) return null;
  const confidence = asNumber(raw.confidence);
  return {
    kind: asString(raw.kind, "claim") || "claim",
    name,
    sourceId: asString(raw.source_id),
    confidence: confidence !== null && confidence >= 0 && confidence <= 1 ? confidence : null,
    outcome: CLAIM_OUTCOMES.includes(raw.outcome as ForensicClaimOutcome)
      ? (raw.outcome as ForensicClaimOutcome)
      : null,
  };
}

function findingFromWire(raw: Record<string, unknown>): AddressForensicFinding | null {
  const kind = optString(raw.kind);
  const summary = optString(raw.summary);
  if (!kind || !summary) return null;
  const confidence = asNumber(raw.confidence);
  return {
    kind,
    // An unknown severity reads as the quietest, never as an alert nobody raised.
    severity: FORENSIC_SEVERITIES[forensicSeverityRank(asString(raw.severity))]!,
    summary,
    foundBy: asString(raw.found_by),
    confidence: confidence !== null && confidence >= 0 && confidence <= 1 ? confidence : null,
    facts: asRecord(raw.facts),
    claims: rowsAt(raw, "claims")
      .slice(0, MAX_CLAIMS)
      .map(claimFromWire)
      .filter((c): c is ForensicClaimRecord => c !== null),
  };
}

/**
 * A subject's forensic record, or `null` when there is none.
 *
 * Tolerant of drift the way the transaction body's reader is: a record of
 * another version is `null` (its fields may mean something else), an unknown
 * severity reads as `info`, a malformed finding, claim or run is dropped, and
 * the lists are bounded however long the wire's are. Never throws.
 */
export function addressForensicsFromWire(raw: unknown): AddressForensics | null {
  const r = asRecord(raw);
  if (asNumber(r.v) !== 1) return null;
  const findings = rowsAt(r, "findings")
    .slice(0, MAX_FINDINGS)
    .map(findingFromWire)
    .filter((f): f is AddressForensicFinding => f !== null)
    // Stable: the writer's order within one severity is kept.
    .sort((a, b) => forensicSeverityRank(b.severity) - forensicSeverityRank(a.severity));
  const runs = (key: string) =>
    rowsAt(r, key)
      .slice(0, MAX_RUNS)
      .map(forensicRunFromWire)
      .filter((x): x is AddressForensicRun => x !== null);
  const stated = optString(r.highest_severity);
  const loudest = findings[0]?.severity ?? null;
  const highestSeverity: ForensicSeverity | null =
    loudest && (!stated || forensicSeverityRank(loudest) >= forensicSeverityRank(stated))
      ? loudest
      : stated && (FORENSIC_SEVERITIES as readonly string[]).includes(stated)
        ? (stated as ForensicSeverity)
        : loudest;
  return {
    subject: asString(r.subject),
    family: optString(r.family),
    profile: optString(r.profile),
    highestSeverity,
    findings,
    findingsTotal: Math.max(asNumber(r.findings_total) ?? 0, findings.length),
    probes: runs("probes"),
    sources: runs("sources"),
  };
}

export function researchRequestFromWire(raw: unknown): ResearchRequest {
  const r = asRecord(raw);
  const counts = asRecord(r.counts);
  const research = asRecord(r.research);
  const context = asRecord(r.context);
  const n = (v: unknown) => asNumber(v) ?? 0;
  return {
    requestId: asString(r.request_id),
    status: oneOf(r.status, STATUSES, "not_started"),
    audience: r.audience === "user" ? "user" : "tenant",
    profile: oneOf(r.profile, PROFILES, "standard"),
    checks: Array.isArray(r.checks)
      ? r.checks.filter((c): c is string => typeof c === "string" && c.length > 0)
      : null,
    // A personal context may come back with its server-filled id or without one.
    context:
      context.kind || context.id
        ? {
            kind: asString(context.kind),
            id: asString(context.id),
            ref: asString(context.ref),
            ...(typeof context.label === "string" && context.label ? { label: context.label } : {}),
          }
        : null,
    requestedBy: r.requested_by == null ? null : asString(r.requested_by),
    createdAt: n(r.created_at),
    updatedAt: n(r.updated_at),
    subjects: rowsAt(r, "subjects").map((s) => ({
      chainId: asString(s.chain_id),
      address: asString(s.address),
      outcome: oneOf(s.outcome, OUTCOMES, "pending"),
      runId: s.run_id == null ? null : asString(s.run_id),
      error: s.error == null ? null : asString(s.error),
      runStatus: s.run_status == null ? null : asString(s.run_status),
      recordsWritten: n(s.records_written),
      recordsReviewQueued: n(s.records_review_queued),
      recordsBelowFloor: n(s.records_below_floor),
      finished: typeof s.finished === "boolean" ? s.finished : null,
      forensicClaimsWritten: n(s.forensic_claims_written),
      forensicClaimsReview: n(s.forensic_claims_review),
      // A subject names its own profile; one that does not ran at the request's.
      profile: oneOf(s.profile, PROFILES, oneOf(r.profile, PROFILES, "standard")),
      forensics: addressForensicsFromWire(s.forensics),
    })),
    counts: {
      runs: n(counts.runs),
      active: n(counts.active),
      succeeded: n(counts.succeeded),
      failed: n(counts.failed),
      cancelled: n(counts.cancelled),
      fanout: n(counts.fanout),
      recordsWritten: n(counts.records_written),
      recordsReviewQueued: n(counts.records_review_queued),
      recordsAutoApproved: n(counts.records_auto_approved),
      recordsWithheldPrivate: n(counts.records_withheld_private),
      recordsBelowFloor: n(counts.records_below_floor),
      forensicClaimsWritten: n(counts.forensic_claims_written),
      forensicClaimsReview: n(counts.forensic_claims_review),
    },
    research: { budget: n(research.budget), used: n(research.used) },
    // Only when the wire says: an older server (and a thin push) says neither.
    ...(asNumber(r.retryable) !== null ? { retryable: n(r.retryable) } : {}),
    ...(asNumber(r.retries) !== null ? { retries: n(r.retries) } : {}),
  };
}

/** Whether a request can still change — what a poll keys on. */
export function isResearchActive(request: Pick<ResearchRequest, "status">): boolean {
  return request.status === "running";
}

export function createApiResearchSource(client: ApiClient): ResearchSource {
  const itemPath = (id: string) => `${RESEARCH_PATH}/${encodeURIComponent(id)}`;
  return {
    async create(input) {
      const body: Record<string, unknown> = {
        subjects: input.subjects.map((s) => ({ chain_id: s.chainId, address: s.address })),
      };
      if (input.context) body.context = researchContextBody(input.context);
      if (input.audience) body.audience = input.audience;
      if (input.profile) body.profile = input.profile;
      // Sent as given, even empty: the server refuses `[]` with a reason, where
      // dropping it here would quietly run the defaults the reader unticked.
      if (input.checks) body.checks = [...input.checks];
      if (input.idempotencyKey) body.idempotency_key = input.idempotencyKey;
      const raw = asRecord(unwrapData(await client.post<unknown>(RESEARCH_PATH, body)));
      client.invalidateMatching(RESEARCH_PATH);
      return { created: raw.created === true, request: researchRequestFromWire(raw.request) };
    },
    async get(requestId, opts) {
      const raw = asRecord(
        unwrapData(await client.get<unknown>(itemPath(requestId), { signal: opts?.signal })),
      );
      return researchRequestFromWire(raw.request);
    },
    async listForContext(context, opts) {
      const query: Record<string, string> = { context: researchContextRef(context) };
      if (opts?.limit) query.limit = String(opts.limit);
      const raw = asRecord(
        unwrapData(await client.get<unknown>(RESEARCH_PATH, { query, signal: opts?.signal })),
      );
      return rowsAt(raw, "requests").map(researchRequestFromWire);
    },
    async retry(requestId) {
      // The item's verb sets the state asked for: `cancelled`, or `running`
      // again — which is the retry.
      const raw = asRecord(
        unwrapData(await client.patch<unknown>(itemPath(requestId), { status: "running" })),
      );
      client.invalidateMatching(RESEARCH_PATH);
      return { started: asNumber(raw.started) ?? 0, request: researchRequestFromWire(raw.request) };
    },
  };
}
